import { RequestStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { ReviewedStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/booking_submission.pb';
import { ScheduleQueries, ScheduleQueryInvalidators } from '@frontend/api-schedule';
import { ScheduleV2 } from '@frontend/api-schedule-v2';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser } from '@frontend/auth-helpers';
import { ActionsUI } from '@frontend/contact-actions';
import { useTranslation } from '@frontend/i18n';
import { emitOpenCallInsightPanel } from '@frontend/inbox-call-item-event-bus';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { ScheduleRequestListItem } from '@frontend/schedule-helpers';
import { useNavigate } from '@frontend/weave-router';
import { useAlert } from '@frontend/design-system';
import { useScheduleActionsContext } from '../../ScheduleActionsContext';

export const useScheduleRequestActions = (listItem: ScheduleRequestListItem) => {
  const { t } = useTranslation('schedule-request');
  const alert = useAlert();
  const navigate = useNavigate();

  const user = getUser();
  const userId = user?.userID ?? '';
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const requestVersion = listItem.linkHash || '';
  const shouldUseV3 = requestVersion !== 'v2' && isScheduleV3FlagEnabled;
  const scheduleRequestId = listItem.id ?? '';
  const sourceDetails = listItem.aiRequestDetails?.sourceDetails;

  const { closeModal } = useScheduleActionsContext();

  const { invalidateScheduleRequestListAndCountV2 } =
    ScheduleQueryInvalidators.useScheduleRequestListAndCountInvalidation();
  const { navigateToThread } = useInboxNavigate();

  const { onClick: messageOnClick } = ActionsUI.actions.useMessageAction();
  const { handleCallActionClick } = ActionsUI.actions.usePhoneCallAction({
    context: {
      personId: listItem.schedulee?.weavePersonId ?? '',
      phoneNumber: listItem.schedulee?.phoneNumber ?? '',
      locationId: listItem.locationId ?? '',
    },
  });

  const deleteV3RequestMutation = SchedulerV3.Mutations.useDeleteBookingSubmissionMutation();
  const deleteV2RequestMutation = ScheduleQueries.useDeleteScheduleRequest();
  const updateV3RequestMutation = SchedulerV3.Mutations.useUpdateBookingSubmissionMutation();
  const updateV2RequestMutation = ScheduleV2.Mutations.useUpdateScheduleRequestMutation();

  const updateV3RequestStatus = async (reviewedStatus: ReviewedStatus) => {
    await updateV3RequestMutation.mutateAsync({
      submissionId: scheduleRequestId,
      reviewedBy: userId,
      reviewedStatus,
    });
  };

  const updateV2RequestStatus = async (status: RequestStatus) => {
    await updateV2RequestMutation.mutateAsync({
      id: listItem.id,
      reviewedBy: userId,
      status,
    });
    invalidateScheduleRequestListAndCountV2();
  };

  const handleMessage = async (updateStatus?: boolean) => {
    try {
      await messageOnClick({
        personId: listItem.schedulee?.weavePersonId,
        personPhone: listItem.schedulee?.phoneNumber ?? '',
        threadGroupId: listItem.locationId ?? '',
        groupIds: [listItem.locationId ?? ''],
      });

      // Update status should only be triggered for preference based requests
      if (updateStatus) {
        if (shouldUseV3) {
          await updateV3RequestStatus(ReviewedStatus.MESSAGED);
        } else {
          await updateV2RequestStatus(RequestStatus.MESSAGED);
        }
      }
    } catch (error) {
      alert.error(t('Failed to send message to patient'));
      throw error;
    }
  };

  const handlePhoneCall = async () => {
    try {
      handleCallActionClick();

      if (shouldUseV3) {
        await updateV3RequestStatus(ReviewedStatus.CALLED);
      } else {
        await updateV2RequestStatus(RequestStatus.CALLED);
      }
    } catch (error) {
      alert.error(t('Failed to make phone call to patient'));
      throw error;
    }
  };

  const handleDeleteRequest = async () => {
    try {
      if (shouldUseV3) {
        await deleteV3RequestMutation.mutateAsync({ submissionIds: [scheduleRequestId] });
      } else {
        await deleteV2RequestMutation.mutateAsync({ id: scheduleRequestId, locationId: listItem.locationId ?? '' });
        invalidateScheduleRequestListAndCountV2();
      }
      alert.success(t('Appointment request deleted successfully'));
    } catch (error) {
      alert.error(t('Failed to delete appointment request'));
      throw error;
    }
  };

  const handleRejectRequest = async () => {
    try {
      if (shouldUseV3) {
        await updateV3RequestStatus(ReviewedStatus.REJECTED);
      } else {
        throw new Error('Rejecting appointment requests is only supported for V3 requests');
      }
      alert.success(t('Appointment request rejected successfully'));
    } catch (error) {
      alert.error(t('Failed to reject appointment request'));
      throw error;
    }
  };

  const handleDismissRequest = async () => {
    try {
      if (shouldUseV3) {
        await updateV3RequestStatus(ReviewedStatus.DISMISSED);
      } else {
        await updateV2RequestStatus(RequestStatus.DISMISSED);
      }
      alert.success(t('Appointment request dismissed successfully'));
    } catch (error) {
      alert.error(t('Failed to dismiss appointment request'));
      throw error;
    }
  };

  const handleCompleteRequest = async () => {
    try {
      if (shouldUseV3) {
        await updateV3RequestStatus(ReviewedStatus.DONE);
      } else {
        await updateV2RequestStatus(RequestStatus.DONE);
      }
      alert.success(t('Appointment request completed successfully'));
    } catch (error) {
      alert.error(t('Failed to complete appointment request'));
      throw error;
    }
  };

  const handleReviewCallLog = () => {
    const channelId = sourceDetails?.channelId;
    const locationId = listItem.locationId;

    if (!channelId || !locationId) return;

    navigate({ to: '/calls/call-history' });

    // Defer panel open until after route transition so the listener is active on the call-history screen.
    setTimeout(() => {
      emitOpenCallInsightPanel({
        channelId,
        includeCallIntelligence: true,
        locationId,
      });
    }, 0);

    closeModal();
  };

  const handleViewConversation = async () => {
    const threadId = sourceDetails?.threadId || sourceDetails?.channelId;
    const groupId = listItem.locationId;
    const messageId = sourceDetails?.messageId;

    if (threadId && groupId) {
      navigateToThread({
        groupId,
        threadId,
        personId: listItem.schedulee?.weavePersonId,
        personPhone: listItem.schedulee?.phoneNumber,
        threadItemId: messageId,
      });
      closeModal();
      return;
    }

    await handleMessage(false);
  };

  return {
    isLoading:
      deleteV3RequestMutation.isLoading ||
      deleteV2RequestMutation.isLoading ||
      updateV3RequestMutation.isLoading ||
      updateV2RequestMutation.isLoading,
    handleMessage,
    handlePhoneCall,
    handleCompleteRequest,
    handleDeleteRequest,
    handleDismissRequest,
    handleRejectRequest,
    handleReviewCallLog,
    handleViewConversation,
  };
};
